<script setup lang="ts">
import { computed } from 'vue'
import DynamicHeading from '@/components/DynamicHeading.vue'
import SubstandardBullets from '@/standards/SubstandardBullets.vue'
import StandardTerms from '@/standards/StandardTerms.vue'
import { LIST_STYLE_CLASS_BY_MARKER_TYPE } from '@/standards/list_markers'
import { standardBlocks } from '@/standards/standard_layouts'
import type { HeadingBlock, HtmlStyle, StandardBlock, TextStyle } from '@/standards/standard_blocks'
import type { StandardDetail } from '@/standards/standard_types'

const props = withDefaults(
  defineProps<{
    standard: StandardDetail
    headingLevel?: number
    editLink?: string
  }>(),
  {
    headingLevel: 3,
    editLink: ''
  }
)

const INDENTED_CLASS = 'mt-3 pl-4 text-sm leading-tight'

const TEXT_CLASS_BY_STYLE: Record<TextStyle, string> = {
  plain: 'mb-2.5',
  flush: 'mb-0',
  indented: `mb-2.5 ${INDENTED_CLASS}`,
  muted: 'mb-2.5 text-gray-700',
  note: 'mb-2.5 text-sm text-gray-700'
}

const HTML_CLASS_BY_STYLE: Record<HtmlStyle, string> = {
  block: '',
  paragraph: TEXT_CLASS_BY_STYLE.plain,
  indentedParagraph: TEXT_CLASS_BY_STYLE.indented
}

const HEADING_CLASS = 'mt-2.5 text-md font-bold leading-snug'

const blocks = computed(() => standardBlocks(props.standard))

const isHeadingShown = (block: StandardBlock): block is HeadingBlock => block.kind === 'heading' && Boolean(block.text)

function headingLevelFor(block: HeadingBlock): number {
  if (block.rank === 'secondary') {
    return props.headingLevel + 1
  }
  return props.headingLevel
}

function headingClassFor(block: HeadingBlock): string[] {
  const marginBelow = block.rank === 'secondary' ? 'mb-0.5' : 'mb-2.5'
  return [HEADING_CLASS, marginBelow]
}
</script>

<template>
  <div class="text-md leading-snug [&_.indent]:block [&_.indent]:px-3.5 [&_.indent]:py-2" data-testid="standard-view">
    <a v-if="editLink" :href="editLink" class="text-sm text-blue-400 hover:text-blue-500 hover:underline">Edit Standard</a>

    <blockquote
      v-if="standard.core_purpose"
      class="my-1 border-l-4 border-gray-200 px-2 py-1 text-sm print:border-0"
      data-testid="core-purpose"
    >
      <strong>Core Purpose</strong>
      <p v-html="standard.core_purpose" />
    </blockquote>

    <template v-for="(block, index) in blocks" :key="index">
      <DynamicHeading v-if="isHeadingShown(block)" :heading-level="headingLevelFor(block)" :class="headingClassFor(block)">
        {{ block.text }}
      </DynamicHeading>
      <p v-else-if="block.kind === 'text'" :class="TEXT_CLASS_BY_STYLE[block.style]">{{ block.text }}</p>
      <div v-else-if="block.kind === 'html' && block.style === 'block'" v-html="block.html" />
      <p v-else-if="block.kind === 'html'" :class="HTML_CLASS_BY_STYLE[block.style]" v-html="block.html" />
      <div v-else-if="block.kind === 'preLine'" class="whitespace-pre-line">{{ block.text }}</div>
      <SubstandardBullets
        v-else-if="block.kind === 'substandards'"
        :ordered-substandards="block.orderedSubstandards"
        :ol-type="block.olType"
        :single-substandard-bullet="block.singleSubstandardBullet"
        :single-substandard-text="block.singleSubstandardText"
      />
      <ol v-else-if="block.kind === 'componentList'" type="a" :class="['mb-2.5 pl-10', LIST_STYLE_CLASS_BY_MARKER_TYPE.a]">
        <li v-for="item in block.items" :key="item.key" v-html="item.text" />
      </ol>
      <ol v-else-if="block.kind === 'indentedList'" class="mb-2.5 list-none">
        <li v-for="item in block.items" :key="item.key" :class="['mb-3', INDENTED_CLASS]">{{ item.text }}</li>
      </ol>
      <StandardTerms v-else-if="block.kind === 'terms'" :terms="block.terms" />
      <br v-else-if="block.kind === 'lineBreak'" />
    </template>
  </div>
</template>
